import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Router, RouterLink } from '@angular/router';
import { UserService, ProfileUpdate } from '../../services/user.service';
import { UserAvatarComponent } from '../../common-components/user-avatar/user-avatar.component';
import { AuthService } from '../../auth/auth.service';
import { ModalButton, ModalComponent } from '../../common-components/modal/modal.component';

@Component({
  selector: 'app-profile-page',
  imports: [TranslocoModule, NgxSkeletonLoaderModule, UserAvatarComponent, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCheckboxModule, RouterLink, ModalComponent],
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.scss'
})
export class ProfilePageComponent implements OnInit {
  private userService = inject(UserService);
  private snackBar = inject(MatSnackBar);
  private transloco = inject(TranslocoService);
  private authService = inject(AuthService);
  private router = inject(Router);

  user = toSignal(this.userService.getUserInfo$());

  xpPercentage = computed(() => {
    const leveling = this.user()?.leveling;
    if (!leveling?.xpToNextLevel) {
      return 0;
    }
    return Math.min(100, Math.round(((leveling.xp || 0) / leveling.xpToNextLevel) * 100));
  });

  saving = signal(false);

  showDeleteConfirmation = signal(false);
  deleteAllQuests = new FormControl(false);
  deleteModalButtons: ModalButton[] = [];

  form = new FormGroup({
    username: new FormControl(''),
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    phoneNumber: new FormControl('', [Validators.pattern(/^\+?[0-9\s\-()]{7,20}$/)])
  });

  searchable = signal(true);

  constructor() {
    effect(() => {
      const user = this.user();
      if (!user) {
        return;
      }
      this.searchable.set(user.privacySettings?.['search'] !== false);
      if (this.form.pristine) {
        this.form.patchValue({
          username: user.username || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phoneNumber: user.phoneNumber || ''
        }, {emitEvent: false});
      }
    });
  }

  ngOnInit(): void {
    this.deleteModalButtons = [
      {
        type: 'secondary',
        text: this.transloco.translate('stay'),
        onClick: () => {
          this.showDeleteConfirmation.set(false);
          this.deleteAllQuests.setValue(false);
        }
      },
      {
        type: 'error',
        text: this.transloco.translate('delete'),
        onClick: () => this.deleteAccount()
      }
    ];
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  private deleteAccount(): void {
    this.userService.deleteUser$(!!this.deleteAllQuests.value).subscribe({
      next: () => {
        this.showDeleteConfirmation.set(false);
        this.logout();
      },
      error: () => this.showErrorSnackbar()
    });
  }

  saveProfile(): void {
    if (this.form.invalid || this.saving()) {
      return;
    }
    const user = this.user();
    const value = this.form.getRawValue();
    const changes: ProfileUpdate = {};
    if (this.form.controls.username.dirty && value.username && value.username !== user?.username) {
      changes.username = value.username;
    }
    if (this.form.controls.firstName.dirty && value.firstName && value.firstName !== user?.firstName) {
      changes.firstName = value.firstName;
    }
    if (this.form.controls.lastName.dirty && value.lastName && value.lastName !== user?.lastName) {
      changes.lastName = value.lastName;
    }
    if (this.form.controls.phoneNumber.dirty && value.phoneNumber && value.phoneNumber !== user?.phoneNumber) {
      changes.phoneNumber = value.phoneNumber;
    }
    if (Object.keys(changes).length === 0) {
      this.form.patchValue({
        username: user?.username || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phoneNumber: user?.phoneNumber || ''
      }, {emitEvent: false});
      this.form.markAsPristine();
      return;
    }
    this.saving.set(true);
    this.userService.updateProfile$(changes).subscribe({
      next: () => {
        this.saving.set(false);
        this.form.markAsPristine();
        this.snackBar.open(
          this.transloco.translate('changesSaved'),
          this.transloco.translate('close'),
          {duration: 3000, verticalPosition: 'bottom', horizontalPosition: 'center'}
        );
      },
      error: () => {
        this.saving.set(false);
        this.showErrorSnackbar();
      }
    });
  }

  toggleSearchable(checked: boolean): void {
    const previous = this.searchable();
    this.searchable.set(checked);
    const settings = {...(this.user()?.privacySettings || {}), search: checked};
    this.userService.updatePrivacySettings$(settings).subscribe({
      error: () => {
        this.searchable.set(previous);
        this.showErrorSnackbar();
      }
    });
  }

  private showErrorSnackbar(): void {
    this.snackBar.open(
      this.transloco.translate('generalErrorText'),
      this.transloco.translate('close'),
      {duration: 5000, verticalPosition: 'bottom', panelClass: 'error-snackbar'}
    );
  }
}
